import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { defaultPredefinedValues, savePredefinedValues } from '../../utils/adminUtils';

console.log('Page PredefinedValuesDashboard chargée');

// Fonction utilitaire pour sauvegarder les valeurs dans un fichier
const saveValuesToFile = async (values) => {
  try {
    const response = await fetch('/api/admin/predefined-values', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(values),
    });
    
    if (!response.ok) {
      throw new Error('Erreur lors de la sauvegarde des valeurs prédéfinies');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Erreur:', error);
    return null;
  }
};

const PredefinedValuesDashboard = () => {
  // État initial avec les valeurs par défaut
  const [values, setValues] = useState(defaultPredefinedValues);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [activeTab, setActiveTab] = useState('ramOptions');
  
  // Charger les valeurs actuelles depuis l'API
  useEffect(() => {
    const loadValues = async () => {
      try {
        // D'abord, essayer de charger depuis l'API
        const response = await fetch('/api/admin/predefined-values');
        if (!response.ok) {
          throw new Error('Erreur lors du chargement des valeurs prédéfinies');
        }
        
        const data = await response.json();
        console.log('Données chargées depuis l\'API:', data);
        setValues(data);
        setLoading(false);
      } catch (error) {
        console.error('Erreur de l\'API, utilisation des valeurs par défaut:', error);
        // En cas d'erreur, utiliser les valeurs par défaut
        setError('Impossible de charger les valeurs depuis le serveur. Utilisation des valeurs par défaut.');
        setTimeout(() => setError(null), 5000);
        setLoading(false);
      }
    };
    
    loadValues();
  }, []);
  
  // Fonction pour ajouter une nouvelle valeur à un tableau
  const addValue = (category, value) => {
    if (!value.trim()) return;
    
    // Éviter les doublons
    if (values[category].includes(value.trim())) {
      setError(`La valeur "${value}" existe déjà dans ${getCategoryName(category)}`);
      setTimeout(() => setError(null), 3000);
      return;
    }
    
    setValues({
      ...values,
      [category]: [...values[category], value.trim()]
    });
  };
  
  // Fonction pour supprimer une valeur d'un tableau
  const removeValue = (category, index) => {
    const newValues = [...values[category]];
    newValues.splice(index, 1);
    
    setValues({
      ...values,
      [category]: newValues
    });
  };
  
  // Fonction pour déplacer une valeur dans un tableau
  const moveValue = (category, index, direction) => {
    if (
      (direction === 'up' && index === 0) || 
      (direction === 'down' && index === values[category].length - 1)
    ) {
      return;
    }
    
    const newValues = [...values[category]];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    
    // Échanger les positions
    [newValues[index], newValues[newIndex]] = [newValues[newIndex], newValues[index]];
    
    setValues({
      ...values,
      [category]: newValues
    });
  };
  
  // Fonction pour mettre à jour une valeur
  const updateValue = (category, index, newValue) => {
    if (!newValue.trim()) return;
    
    // Éviter les doublons (sauf si c'est la même valeur à la même position)
    if (values[category].includes(newValue.trim()) && values[category][index] !== newValue.trim()) {
      setError(`La valeur "${newValue}" existe déjà dans ${getCategoryName(category)}`);
      setTimeout(() => setError(null), 3000);
      return;
    }
    
    const newValues = [...values[category]];
    newValues[index] = newValue.trim();
    
    setValues({
      ...values,
      [category]: newValues
    });
  };
  
  // Fonction pour sauvegarder toutes les valeurs
  const saveAllValues = async () => {
    setLoading(true);
    try {
      const result = await savePredefinedValues(values);
      if (result) {
        setSuccess('Valeurs prédéfinies enregistrées avec succès');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError('Erreur lors de l\'enregistrement des valeurs prédéfinies');
        setTimeout(() => setError(null), 3000);
      }
    } catch (error) {
      console.error('Erreur:', error);
      setError('Erreur lors de l\'enregistrement des valeurs prédéfinies');
      setTimeout(() => setError(null), 3000);
    }
    setLoading(false);
  };
  
  // Fonction pour obtenir le nom convivial d'une catégorie
  const getCategoryName = (category) => {
    const names = {
      ramOptions: 'Options de RAM',
      cpuOptions: 'Options de CPU',
      diskOptions: 'Options de disque',
      osOptions: 'Options d\'OS',
      mariadbVersionOptions: 'Versions MariaDB',
      apacheVersionOptions: 'Versions Apache',
      nodejsVersionOptions: 'Versions NodeJS'
    };
    
    return names[category] || category;
  };
  
  // Gérer le nouvel ajout
  const [newValue, setNewValue] = useState('');
  
  const handleAddValue = (e) => {
    e.preventDefault();
    addValue(activeTab, newValue);
    setNewValue('');
  };

  if (loading && !values.ramOptions.length) {
    return <div className="text-center py-10 bg-yellow-300 border-4 border-red-500">
      <h1 className="text-2xl font-bold">Chargement des valeurs prédéfinies...</h1>
      <p>Si cette page reste bloquée, vérifiez la console pour les erreurs.</p>
    </div>;
  }

  return (
    <div className="container mx-auto p-4">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold">Gestion des valeurs prédéfinies</h1>
        <Link to="/" className="text-blue-600 hover:underline">
          Retour à l'accueil
        </Link>
      </div>
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}
      
      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
          {success}
        </div>
      )}
      
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="flex border-b">
          {Object.keys(values).map((category) => (
            <button
              key={category}
              className={`px-4 py-2 ${
                activeTab === category
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
              onClick={() => setActiveTab(category)}
            >
              {getCategoryName(category)}
            </button>
          ))}
        </div>
        
        <div className="p-4">
          <h2 className="text-xl font-semibold mb-4">
            {getCategoryName(activeTab)}
          </h2>
          
          <form onSubmit={handleAddValue} className="mb-4 flex">
            <input
              type="text"
              value={newValue}
              onChange={(e) => setNewValue(e.target.value)}
              className="flex-1 p-2 border border-gray-300 rounded-l"
              placeholder={`Ajouter une nouvelle valeur à ${getCategoryName(activeTab)}`}
            />
            <button
              type="submit"
              className="bg-green-600 text-white px-4 py-2 rounded-r hover:bg-green-700"
            >
              Ajouter
            </button>
          </form>
          
          <div className="border rounded overflow-hidden">
            <table className="min-w-full">
              <thead className="bg-gray-100">
                <tr>
                  <th className="py-2 px-4 text-left w-10">#</th>
                  <th className="py-2 px-4 text-left">Valeur</th>
                  <th className="py-2 px-4 text-right w-32">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {values[activeTab].map((value, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="py-2 px-4">{index + 1}</td>
                    <td className="py-2 px-4">
                      <input
                        type="text"
                        value={value}
                        onChange={(e) => updateValue(activeTab, index, e.target.value)}
                        className="w-full p-1 border border-gray-300 rounded"
                      />
                    </td>
                    <td className="py-2 px-4 text-right space-x-1">
                      <button
                        onClick={() => moveValue(activeTab, index, 'up')}
                        disabled={index === 0}
                        className={`px-2 py-1 text-xs rounded ${
                          index === 0
                            ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                            : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                        }`}
                      >
                        ▲
                      </button>
                      <button
                        onClick={() => moveValue(activeTab, index, 'down')}
                        disabled={index === values[activeTab].length - 1}
                        className={`px-2 py-1 text-xs rounded ${
                          index === values[activeTab].length - 1
                            ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                            : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                        }`}
                      >
                        ▼
                      </button>
                      <button
                        onClick={() => removeValue(activeTab, index)}
                        className="bg-red-100 text-red-700 px-2 py-1 text-xs rounded hover:bg-red-200"
                      >
                        ✕
                      </button>
                    </td>
                  </tr>
                ))}
                {values[activeTab].length === 0 && (
                  <tr>
                    <td colSpan="3" className="py-4 text-center text-gray-500">
                      Aucune valeur pour {getCategoryName(activeTab)}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          
          <div className="mt-6 flex justify-end">
            <button
              onClick={saveAllValues}
              disabled={loading}
              className={`px-4 py-2 rounded ${
                loading
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {loading ? 'Enregistrement...' : 'Enregistrer les modifications'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PredefinedValuesDashboard;
